import React from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WeightPanel from '../opportunity_calculator/WeightPanel';
import BreakpointEditor from '../opportunity_calculator/BreakpointEditor';
import { weightSum } from '../opportunity_calculator/indicatorConfig';
import { formatPercent } from '../opportunity_calculator/formatters';
import { RISK_CATEGORIES } from './riskIndicatorConfig';
import { DEFAULT_TIER_THRESHOLDS, RISK_TIERS } from './riskTiers';

const ROW_SX = { display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 2, py: 1.25 };
const TOGGLE_SX = { py: 0.25, px: 1.25, fontSize: '0.7rem', lineHeight: 1.6, textTransform: 'none' };

const ControlLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      minWidth: 104,
      fontSize: '0.65rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: 'text.secondary',
      flexShrink: 0,
    }}
  >
    {children}
  </Typography>
);

const TIER_FIELDS = [
  { key: 'moderate', label: 'Moderate from' },
  { key: 'high', label: 'High from' },
  { key: 'veryHigh', label: 'Very High from' },
];

const RiskModelBar = ({
  open,
  onToggle,
  indicators,
  weights,
  onWeightChange,
  onResetWeights,
  thresholds,
  onThresholdChange,
  renormalize,
  onRenormalize,
  breakpointsById,
  onBreakpointChange,
  onBreakpointReset,
}) => {
  const sum = weightSum(weights);
  const tiersAreDefault = TIER_FIELDS.every(({ key }) => thresholds[key] === DEFAULT_TIER_THRESHOLDS[key]);

  return (
    <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <TuneIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700} sx={{ whiteSpace: 'nowrap' }}>
          Risk scoring model
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1, minWidth: 0 }}>
          {!open && (
            <>
              <Chip
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
                label={`Tiers: Moderate ${thresholds.moderate}+ · High ${thresholds.high}+ · Very High ${thresholds.veryHigh}+`}
              />
              <Chip
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
                label={renormalize ? 'Renormalized over available' : 'Missing scored as zero'}
              />
            </>
          )}
        </Box>
        <IconButton size="small" aria-label={open ? 'Collapse risk scoring model' : 'Expand risk scoring model'}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={open} unmountOnExit>
        <Divider />
        <Box sx={ROW_SX}>
          <ControlLabel>Risk tiers</ControlLabel>
          {TIER_FIELDS.map(({ key, label }) => (
            <TextField
              key={key}
              size="small"
              type="number"
              label={label}
              value={thresholds[key]}
              onChange={(event) => {
                const next = Number(event.target.value);
                if (Number.isFinite(next)) onThresholdChange(key, Math.max(0, Math.min(100, next)));
              }}
              inputProps={{ min: 0, max: 100, step: 5, 'aria-label': `${label} risk score` }}
              sx={{ width: 140 }}
            />
          ))}
          <Stack direction="row" spacing={0.5}>
            {RISK_TIERS.map((tier) => (
              <Chip
                key={tier.id}
                size="small"
                label={tier.label}
                sx={{ bgcolor: tier.color, color: '#fff', height: 22, fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
          {!tiersAreDefault && (
            <Button size="small" onClick={() => onThresholdChange('reset')}>
              Reset tiers
            </Button>
          )}
        </Box>

        <Divider />
        <Box sx={ROW_SX}>
          <ControlLabel>Missing data</ControlLabel>
          <Tooltip title="New schools have no 5-year history and some schools have no state rating. Renormalized spreads those weights over the measures that do have data, so scores stay comparable.">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={renormalize ? 'renormalized' : 'raw'}
              onChange={(_, value) => {
                if (value !== null) onRenormalize(value === 'renormalized');
              }}
            >
              <ToggleButton value="renormalized" sx={TOGGLE_SX}>
                Renormalized
              </ToggleButton>
              <ToggleButton value="raw" sx={TOGGLE_SX}>
                Missing = 0
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
          <Chip
            size="small"
            variant="outlined"
            label={`Weights sum ${formatPercent(sum, 0)} (auto-normalized to 100%)`}
            sx={{ height: 24 }}
          />
          <Button size="small" onClick={onResetWeights}>
            Reset weights
          </Button>
        </Box>

        <Divider />
        <Box sx={{ px: 2, py: 1.75 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Each measure scores 1 (lowest risk) to 5 (highest risk) from its breakpoints. The weighted
            blend is shown on a 100-point risk scale.
          </Typography>
          <WeightPanel
            indicators={indicators}
            weights={weights}
            onChange={onWeightChange}
            categories={RISK_CATEGORIES}
          />
        </Box>

        <Divider />
        <BreakpointEditor
          indicators={indicators}
          breakpointsById={breakpointsById}
          onChange={onBreakpointChange}
          onReset={onBreakpointReset}
          directionNote={() => ' (values below a cutoff are riskier)'}
          description="Values below the Score 5 cutoff get a 5 (highest risk); values above the Score 2 cutoff get a 1. Percent measures are fractions (-0.1 = -10%)."
        />
      </Collapse>
    </Paper>
  );
};

export default RiskModelBar;
